import {
  NoddiAsyncMultiSelect,
  NoddiAsyncMultiSelectProps
} from '@ui-components/Inputs/NoddiMultiSelect/NoddiAsyncMultiSelect';
import { flattenMultiSelectOptions, MultiSelectGroup, MultiSelectOption } from '../../../base/multi-select-utils';
import { useFieldContext } from '../formContext';
import { TanstackFieldError } from '../TanstackFieldError';
import { useTanstackFieldAria } from '../useTanstackFieldAria';

type Props = Omit<NoddiAsyncMultiSelectProps, 'onValueChange' | 'defaultOptions'> & {
  onSelectionSideEffect?: (options: MultiSelectOption[]) => void;
};

export const TanstackFormAsyncMultiSelect = ({ options, onSelectionSideEffect, ...rest }: Props) => {
  const field = useFieldContext<MultiSelectOption[]>();
  const fieldAria = useTanstackFieldAria(field, rest['aria-describedby']);

  return (
    <div>
      <NoddiAsyncMultiSelect
        {...rest}
        id={rest.id ?? field.name}
        name={field.name}
        {...fieldAria}
        options={options}
        defaultOptions={field.state.value}
        onValueChange={(values) => {
          const selectedOptions = createSelectedOptions(values, options, field.state.value);
          field.handleChange(selectedOptions);
          onSelectionSideEffect?.(selectedOptions);
        }}
      />
      <TanstackFieldError field={field} />
    </div>
  );
};

const createSelectedOptions = (
  selectedValues: (string | number)[],
  currentOptions: MultiSelectOption[] | MultiSelectGroup[] = [],
  previouslySelected: MultiSelectOption[] = []
): MultiSelectOption[] => {
  const optionMap = new Map<string, MultiSelectOption>();

  flattenMultiSelectOptions(currentOptions).forEach((option) => {
    optionMap.set(String(option.value), option);
  });

  previouslySelected.forEach((option) => {
    const key = String(option.value);
    if (!optionMap.has(key)) {
      optionMap.set(key, option);
    }
  });

  return selectedValues
    .map((value) => optionMap.get(String(value)))
    .filter((option): option is MultiSelectOption => !!option);
};
